import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, Calendar, Edit2, Trash2, ExternalLink, Eye, Globe, CheckCircle2 } from 'lucide-react';
import Modal from '../../components/Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function EventsPage() {
    const { user, role } = useOutletContext() || {};
    const isClient = role === 'client';
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        event_type: 'appointment',
        oem_id: '',
        group_id: '',
        appointment_duration_minutes: 30,
        gap_duration_minutes: 15,
        is_active: true,
        is_public: true,
        custom_hostname: ''
    });
    const [oems, setOems] = useState([]);
    const [groups, setGroups] = useState([]);
    const [existingDomains, setExistingDomains] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchEvents();
        fetchOEMs();
        fetchGroups();
        fetchExistingDomains();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events?is_active=true&_t=${Date.now()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                cache: 'no-cache'
            });
            if (response.ok) {
                const data = await response.json();
                setEvents(data);
            } else {
                setError('Failed to fetch events');
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            setError('Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    const fetchOEMs = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/oems`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setOems(data.filter(o => o.is_active !== false));
            }
        } catch (error) {
            console.error('Error fetching OEMs:', error);
        }
    };

    const fetchGroups = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/groups`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setGroups(data);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    const fetchExistingDomains = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const domains = [...new Set((data || []).map(e => (e.custom_hostname || '').toLowerCase()).filter(Boolean))].sort();
                setExistingDomains(domains);
            }
        } catch (err) {
            console.error('Error fetching existing domains:', err);
        }
    };

    const handleAdd = () => {
        setEditingEvent(null);
        setFormData({
            name: '',
            description: '',
            event_type: 'appointment',
            oem_id: '',
            group_id: isClient && user?.group_id ? user.group_id : '',
            appointment_duration_minutes: 30,
            gap_duration_minutes: 15,
            is_active: true,
            is_public: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (event) => {
        // Navigate to configuration page instead of opening modal
        navigate(`/events/${event.id}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('accessToken');
            const url = editingEvent 
                ? `${API_URL}/events/${editingEvent.id}`
                : `${API_URL}/events`;
            const method = editingEvent ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    oem_id: formData.oem_id || null,
                    group_id: formData.group_id || null,
                    custom_hostname: formData.custom_hostname?.trim() || null
                })
            });

            if (response.ok) {
                setIsModalOpen(false);
                fetchEvents();
            } else {
                const errorData = await response.json();
                setError(errorData.message || errorData.error || 'Failed to save event');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            setError('Failed to save event');
        }
    };

    const handleDelete = async (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) {
            return;
        }

        try {
            setError('');
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${eventId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchEvents();
            } else {
                const errBody = await response.json().catch(() => ({}));
                const message = errBody.error || errBody.message || `Failed to delete event (${response.status})`;
                setError(message);
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            setError(error.message || 'Failed to delete event');
        }
    };

    const handleConfigure = (eventId) => {
        navigate(`/events/${eventId}`);
    };

    const handleViewPublic = (event) => {
        // Prefer short_code if available, otherwise use slug
        const identifier = event.short_code || event.slug;
        if (identifier) {
            window.open(`/events/public/${identifier}`, '_blank');
        } else {
            alert('Event does not have a public link. Please configure the event first.');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading events...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Events</h1>
                    <p className="text-gray-600 mt-1">Manage your event bookings and appointments</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/events/domains')}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        title="Manage Custom Domains & Nginx"
                    >
                        <Globe size={20} />
                        Domain Management
                    </button>
                    <button
                        onClick={handleAdd}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <Plus size={20} />
                        Add Event
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Owner
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Link
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Appointments
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {events.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                    No events found. Create your first event to get started.
                                </td>
                            </tr>
                        ) : (
                            events.map((event) => (
                                <tr key={event.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{event.name}</div>
                                        {event.description && (
                                            <div className="text-sm text-gray-500 truncate max-w-xs">
                                                {event.description}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                            {event.event_type || 'appointment'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {event.oem_name && <div>OEM: {event.oem_name}</div>}
                                        {event.group_name && <div>Group: {event.group_name}</div>}
                                        {!event.oem_name && !event.group_name && <div>Independent</div>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {event.short_code ? (
                                            <div className="flex flex-col gap-1">
                                                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                                    {event.short_code}
                                                </code>
                                                <span className="text-xs text-gray-400">Short link</span>
                                            </div>
                                        ) : event.slug ? (
                                            <div className="flex flex-col gap-1">
                                                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate max-w-xs">
                                                    {event.slug}
                                                </code>
                                                <span className="text-xs text-gray-400">Slug</span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs">No link</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {event.appointment_count || 0} appointments
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            event.is_active 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {event.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                        {event.is_public && (
                                            <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                Public
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleViewPublic(event)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="View Public Page"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(event)}
                                                className="text-indigo-600 hover:text-indigo-900"
                                                title="Edit/Configure"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(event.id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingEvent ? 'Edit Event' : 'Add Event'}
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Event Name *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="3"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Event Type
                        </label>
                        <select
                            value={formData.event_type}
                            onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="appointment">Appointment</option>
                            <option value="test_drive">Test Drive</option>
                            <option value="service">Service</option>
                            <option value="consultation">Consultation</option>
                            <option value="viewing">Vehicle Viewing</option>
                        </select>
                    </div>

                    {!isClient && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                OEM
                            </label>
                            <select
                                value={formData.oem_id}
                                onChange={(e) => setFormData({ ...formData, oem_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">None (Independent)</option>
                                {oems.map(oem => (
                                    <option key={oem.id} value={oem.id}>{oem.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Group
                            </label>
                            <div className="relative">
                                <select
                                    value={formData.group_id}
                                    onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">None</option>
                                    {groups.map(group => {
                                        const isSynced = group.is_voicebox_synced === true;
                                        return (
                                            <option key={group.id} value={group.id}>
                                                {group.name}{!isSynced && ' (Not Synced)'}
                                            </option>
                                        );
                                    })}
                                </select>
                                {formData.group_id && (() => {
                                    const selectedGroup = groups.find(g => g.id === formData.group_id);
                                    const isSynced = selectedGroup?.is_voicebox_synced === true;
                                    return isSynced ? (
                                        <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <CheckCircle2 size={16} className="text-green-600" />
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                                Custom Domain (optional)
                            </label>
                            <button
                                type="button"
                                onClick={() => navigate('/events/domains')}
                                className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                            >
                                <Globe className="w-3 h-3" />
                                Manage Domains
                            </button>
                        </div>
                        {existingDomains.length > 0 && (
                            <div className="mb-2">
                                <label className="block text-xs text-gray-500 mb-1">Or select existing portal domain</label>
                                <select
                                    value=""
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) setFormData({ ...formData, custom_hostname: val });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                    <option value="">-- Select existing domain --</option>
                                    {existingDomains.map((d) => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Selecting an existing domain adds this event to the portal.</p>
                            </div>
                        )}
                        <input
                            type="text"
                            value={formData.custom_hostname}
                            onChange={(e) => setFormData({ ...formData, custom_hostname: e.target.value.trim() })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="e.g., booking.dealership.com"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Appointment Duration (minutes)
                            </label>
                            <input
                                type="number"
                                value={formData.appointment_duration_minutes}
                                onChange={(e) => setFormData({ ...formData, appointment_duration_minutes: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                min="15"
                                step="15"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Gap Duration (minutes)
                            </label>
                            <input
                                type="number"
                                value={formData.gap_duration_minutes}
                                onChange={(e) => setFormData({ ...formData, gap_duration_minutes: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                min="0"
                                step="5"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Active</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formData.is_public}
                                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Public</span>
                        </label>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            {editingEvent ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

